import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  CircularProgress,
} from "@mui/material";
import React, { forwardRef, useRef, useState } from "react";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { StyledTextField } from "./TextFieldStyle";

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.error === nextProps.error &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.options === nextProps.options &&
    prevProps.loading === nextProps.loading &&
    prevProps.loadingText === nextProps.loadingText &&
    prevProps.noOptionsText === nextProps.noOptionsText &&
    prevProps.hasNextPage === nextProps.hasNextPage &&
    prevProps.hasPrevPage === nextProps.hasPrevPage
  );
};

const CustomTextFieldBase = forwardRef(
  (
    {
      options = [],
      disabled = false,
      loading = false,
      loadingText = "Loading...",
      noOptionsText = "No options",
      fullWidth = false,
      label = "",
      placeholder = " ",
      size = "small",
      type = "text",
      maxRows,
      className,
      spacing,
      inputProps = {},
      backgroundColor,
      hideCountButton = false,
      invertBorder = false,
      error = false,
      errorTextClassName = "",
      hidden = false,
      control,
      name = "test",
      rules = {},
      readOnly = false,
      style,
      multipleLine = false,
      required = false,
      helperText,
      onClick,
      onFocus,
      defaultValue,
      handleCopy,
      handlePast,
      handleCut,
      autocomplete,
      contentEditable,
      classNameFormGrid,
      startIcon = null,
      endIcon = null,
      tabIndex,
      formType = "defaultForm",
      maxLength,
      showCharCount = false,
      onPageChange,
      hasNextPage = false,
      hasPrevPage = false,
      onInputChange,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputRef = useRef(null);
    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleIconClick = () => {
      if (inputRef.current && inputRef.current.type === "date") {
        inputRef.current.showPicker();
      }
    };


    const charCount = props.value ? props.value.toString().length : 0;
    const remainingChars = maxLength ? maxLength - charCount : null;
    const showCount = maxLength && showCharCount && !disabled && !readOnly;


    const getReadOnlyStyles = () => {
      if (readOnly) {
        return {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          cursor: "default",
          "& .MuiInputBase-input": {
            cursor: "default",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
            color: "rgba(0, 0, 0, 0.87)",
          },
          "& fieldset": {
            borderColor: "rgba(0, 0, 0, 0.23) !important",
          },
          "&:hover fieldset": {
            borderColor: "rgba(0, 0, 0, 0.23) !important",
          },
        };
      }
      return {};
    };

    const getDisabledStyles = () => {
      if (disabled) {
        return {
          opacity: 0.6,
          "& .MuiInputBase-input": {
            WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
            color: "rgba(0, 0, 0, 0.38)",
          },
          "& fieldset": {
            borderColor: "rgba(0, 0, 0, 0.23) !important",
          },
          "&:hover fieldset": {
            borderColor: "rgba(0, 0, 0, 0.23) !important",
          },
          "& label": {
            color: "rgba(0, 0, 0, 0.38) !important",
          },
          "&:hover label": {
            color: "rgba(0, 0, 0, 0.38) !important",
          },
        };
      }
      return {};
    };

    const processedOptions = React.useMemo(() => {
      if (!Array.isArray(options)) return [];

      return options.map((option) => {
        if (typeof option === "object" && option !== null) {
          return option;
        }
        return { value: option, label: option };
      });
    }, [options]);

    const selectedOption = React.useMemo(() => {
      if (!props.value) return null;
      return processedOptions.find((opt) => opt.value === props.value) || null;
    }, [props.value, processedOptions]);

    const handleAutocompleteChange = (event, newValue) => {
      if (!newValue) {
        props.onChange({ target: { value: "" } });
        return;
      }

      props.onChange({ target: { value: newValue.value } });
    };

    return (
      <Grid
        className={`form-input-container ${hidden ? "hidden" : ""
          } ${classNameFormGrid}`}
        container
        flexDirection="column"
        spacing={spacing}
      >
        <Grid item>
          {type === "select" ? (
            <FormControl fullWidth error={!!error}>
              <Autocomplete
                options={processedOptions}
                getOptionLabel={(option) => option.label}
                value={selectedOption}
                onChange={handleAutocompleteChange}
                disabled={disabled}
                readOnly={readOnly}
                loading={loading}
                loadingText={loadingText}
                noOptionsText={noOptionsText}
                disableClearable={required || readOnly}
                tabIndex={disabled || readOnly ? -1 : tabIndex}
                onInputChange={onInputChange}
                PaperComponent={({ children }) => (
                  <Paper elevation={8}>
                    {children}
                    {(hasNextPage || hasPrevPage) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          p: 1,
                          borderTop: "1px solid #eee",
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        <Button
                          size="small"
                          disabled={!hasPrevPage}
                          onClick={() => onPageChange && onPageChange("prev")}
                          sx={{ fontSize: "0.75rem", color: "#179b9b" }}
                        >
                          Previous / முந்தைய
                        </Button>
                        <Button
                          size="small"
                          disabled={!hasNextPage}
                          onClick={() => onPageChange && onPageChange("next")}
                          sx={{ fontSize: "0.75rem", color: "#179b9b" }}
                        >
                          Next / அடுத்தது
                        </Button>
                      </Box>
                    )}
                  </Paper>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      required ? (
                        <>
                          {label}
                          <span style={{ color: "red" }}> *</span>
                        </>
                      ) : (
                        label
                      )
                    }
                    placeholder={placeholder}
                    error={!!error}
                    helperText={helperText}
                    disabled={disabled}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: readOnly,
                      startAdornment: (
                        <>
                          {startIcon && (
                            <InputAdornment
                              position="start"
                              sx={{ marginLeft: "10px", marginRight: "8px" }}
                            >
                              {startIcon}
                            </InputAdornment>
                          )}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                          {endIcon && (
                            <InputAdornment
                              position="end"
                              sx={{ marginRight: "10px" }}
                            >
                              {endIcon}
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      tabIndex: disabled || readOnly ? -1 : tabIndex,
                    }}
                    sx={{
                      "& input": {
                        marginRight: "20px",
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        color: "rgb(68, 68, 69) !important",
                        height: "30px",
                        border: "none",
                        outline: "none",
                      },
                      "& fieldset": {
                        borderRadius: "5px",
                        border: "1px solid rgba(181, 188, 188, 0.61) !important",
                        color: "rgb(235, 235, 241) !important",
                      },
                      "&:hover fieldset": {
                        borderColor: "#179b9b !important",
                      },
                      "&& .MuiInputBase-root": {
                        color: "#444445 !important",
                        paddingLeft: startIcon ? "0" : "14px",
                        "& .MuiSelect-select": {
                          paddingLeft: startIcon ? "0" : "inherit",
                        },
                        display: "flex",
                        alignItems: "center",
                      },
                      "& label": {
                        fontSize: "1rem !important",
                        lineHeight: "1.6rem !important",
                        letterSpacing: "0.02rem !important",
                        color: "#000 !important",
                      },
                      "&:hover label": {
                        color: "#179b9b !important",
                      },
                      "& .Mui-focused label": {
                        color: "#179b9b !important",
                      },
                      "& .MuiAutocomplete-option:hover": {
                        backgroundColor: "rgba(23, 155, 155, 0.1) !important",
                        color: "#179b9b !important",
                      },
                      "& .Mui-selected, & .Mui-selected:hover": {
                        backgroundColor: "rgba(23, 155, 155, 0.2) !important",
                        color: "#179b9b !important",
                      },
                      ...getReadOnlyStyles(),
                      ...getDisabledStyles(),
                    }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    padding: "4px 0",
                    "& .MuiAutocomplete-option": {
                      minHeight: "32px",
                      padding: "2px 16px",
                      "&:hover": {
                        color: "#179b9b !important",
                        backgroundColor: "rgba(23, 155, 155, 0.1) !important",
                      },
                      "&.Mui-selected": {
                        color: "#179b9b !important",
                        backgroundColor: "rgba(23, 155, 155, 0.2) !important",
                      }
                    },
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c0c0c0",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#acaaaaf7",
                    },
                    maxHeight: 180,
                    overflowY: "auto",
                    fontSize: "0.875rem",
                  }
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li
                      key={key}
                      {...optionProps}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        fontSize: "0.875rem",
                        minHeight: "32px"
                      }}
                    >
                      {option.label}
                    </li>
                  );
                }}
              />
            </FormControl>
          ) : (
            <StyledTextField
              label={
                required ? (
                  <>
                    {label}
                    <span style={{ color: "red" }}> *</span>
                  </>
                ) : (
                  label
                )
              }
              id="outlined-basic"
              variant="outlined"
              style={style}
              rows={maxRows}
              multiline={multipleLine}
              className={`form-input w-100 ${className} ${hideCountButton ? "hide-count-button" : ""
                } ${invertBorder ? "invert-border" : "normal-border"} ${multipleLine ? "multiple-line" : ""
                } ${readOnly ? "read-only" : ""} ${disabled ? "disabled" : ""}`}
              fullWidth={fullWidth}
              size={size}
              placeholder={placeholder}
              type={
                type === "password"
                  ? showPassword
                    ? "text"
                    : "password"
                  : type
              }
              inputRef={inputRef}
              InputProps={{
                startAdornment: startIcon ? (
                  <InputAdornment position="start" sx={{ marginLeft: "10px" }}>
                    {startIcon}
                  </InputAdornment>
                ) : type === "dateText" ? (
                  <InputAdornment position="start" sx={{ marginLeft: "10px" }}>
                    <IconButton edge="start" onClick={handleIconClick} disabled={disabled || readOnly}>
                      <CalendarTodayIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                endAdornment: endIcon ? (
                  <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                    {endIcon}
                  </InputAdornment>
                ) : type === "password" && !disabled && !readOnly ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                      sx={{ marginRight: "1px", color: "#ccc" }}
                      disabled={disabled || readOnly}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) : null,
                ...inputProps,
                inputRef: ref,
                readOnly: readOnly,
              }}
              inputProps={{
                tabIndex: disabled || readOnly ? -1 : tabIndex,
                maxLength: maxLength,
                ...(inputProps.inputProps || {}),
              }}
              disabled={disabled}
              value={props.value || ""}
              onChange={props.onChange}
              onBlur={props.onBlur}
              sx={{
                backgroundColor: backgroundColor || "black",
                "& fieldset": {
                  borderRadius: "10px",
                  border: "1px solid rgba(181, 188, 188, 0.47) !important",
                },
                "&:hover fieldset": {
                  borderColor: "#179b9b !important",
                },
                "& label": {
                  color: "#000 !important",
                },
                "&:hover label": {
                  color: "#179b9b !important",
                },
                "& .Mui-focused label": {
                  color: "#179b9b !important",
                },
                ...getReadOnlyStyles(),
                ...getDisabledStyles(),
              }}
              error={!!error}
              helperText={showCount ?
                `${helperText ? `${helperText} | ` : ''}${remainingChars} characters remaining` :
                helperText
              }
              readOnly={readOnly}
              onClick={disabled || readOnly ? undefined : onClick}
              onFocus={disabled || readOnly ? undefined : onFocus}
              onCut={disabled || readOnly ? undefined : handleCut}
              onCopy={disabled || readOnly ? undefined : handleCopy}
              onPaste={disabled || readOnly ? undefined : handlePast}
              autoComplete={autocomplete}
              contentEditable={contentEditable && !disabled && !readOnly}
            />
          )}
        </Grid>
      </Grid>
    );
  }
);

const CustomTextField = React.memo(CustomTextFieldBase, areEqual);

export default CustomTextField;